import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Button,
  Container,
  Box,
  useTheme,
  useMediaQuery,
  keyframes,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Drawer,
  List,
  ListItem,
} from '@mui/material';
import {
  School,
  Person,
  Groups,
  ArrowDropDown,
  Menu as MenuIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

const Navbar = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState(null);
  const [moreMenuAnchor, setMoreMenuAnchor] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const mainNavItems = [
    { name: 'Home', path: '/' },
    { name: 'People', path: '/people', hasDropdown: true },
    { name: 'Achievements', path: '/achievements' },
    { name: 'Academics', path: '/academics' },
    { name: 'About Us', path: '/about' },
    { name: 'Research', path: '/research' },
    { name: 'Placement', path: '/placement' },
    { name: 'Events', path: '/events' },
    { name: 'News', path: '/news' },
    { name: 'Contact', path: '/contact' }
  ];

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMoreMenuOpen = (event) => {
    setMoreMenuAnchor(event.currentTarget);
  };

  const handleMoreMenuClose = () => {
    setMoreMenuAnchor(null);
  };

  const handleMenuItemClick = (path) => {
    navigate(path);
    handleMenuClose();
    handleMoreMenuClose();
  };

  const handleDrawerOpen = () => setDrawerOpen(true);
  const handleDrawerClose = () => setDrawerOpen(false);

  // Capitalize function
  const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

  return (
    <AppBar
      position="sticky"
      sx={{
        minHeight: '70px',
        height: '70px',
        justifyContent: 'center',
        boxShadow: '0px 2px 10px rgba(0,0,0,0.1)',
        backgroundColor: '#1F2937',
        width: '100%',
        left: 0,
        right: 0,
        margin: 0,
        padding: 0,
      }}
    >
      <Container maxWidth="xl" disableGutters sx={{ px: 0, mx: 0, width: '100%' }}>
        <Toolbar
          disableGutters
          sx={{
            minHeight: '70px',
            height: '70px',
            px: { xs: 0, md: 3 },
            py: 0,
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            margin: 0,
          }}
        >
          {/* Left Logo in Square Box */}
          <Box 
            onClick={() => navigate('/')}
            sx={{ 
              width: { xs: 48, md: 56 }, 
              height: { xs: 48, md: 56 }, 
              bgcolor: 'white', 
              borderRadius: 2, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              boxShadow: '0px 4px 12px rgba(0,0,0,0.1)',
              flexShrink: 0,
              cursor: 'pointer',
              ml: { xs: 2, md: 0 },
            }}
          >
            <img
              src="/cselogo.png"
              alt="CSE Department Logo"
              style={{ height: '80%', width: '80%', objectFit: 'contain' }}
            />
          </Box>

          {/* Nav Links - centered (desktop only) */}
          <Box sx={{ 
            display: { xs: 'none', md: 'flex' }, 
            gap: 0.5,
            flexGrow: 1, 
            justifyContent: 'center',
            ml: 2,
            mr: 2,
            width: '100%',
            minWidth: 0,
            flexWrap: 'nowrap',
            overflowX: 'auto',
            scrollbarWidth: 'none', // hide scrollbar in Firefox
            '&::-webkit-scrollbar': { display: 'none' }, // hide scrollbar in Chrome
          }}>
            {mainNavItems.map((item) => (
              <Button
                key={item.name}
                component={item.hasDropdown ? 'div' : RouterLink}
                to={!item.hasDropdown ? item.path : undefined}
                onClick={item.hasDropdown ? handleMenuOpen : undefined}
                sx={{
                  color: 'white',
                  fontSize: { xs: '0.9rem', md: '0.95rem' },
                  py: 1,
                  px: 1.5,
                  minWidth: 0,
                  lineHeight: 1,
                  height: '40px',
                  fontWeight: 500,
                  letterSpacing: 0.5,
                  position: 'relative',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                {capitalize(item.name)}
                {item.hasDropdown && <ArrowDropDown sx={{ ml: 0.5 }} />}
              </Button>
            ))}
          </Box>

          {/* Hamburger Menu Icon (mobile only) */}
          <IconButton
            color="inherit"
            onClick={handleDrawerOpen}
            sx={{
              display: { xs: 'flex', md: 'none' },
              color: 'white',
              ml: 1,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            <MenuIcon />
          </IconButton>

          {/* Right Logo in Square Box (desktop only) */}
          <Box 
            onClick={() => window.open('https://www.mbm.ac.in/', '_blank')}
            sx={{ 
              width: { xs: 48, md: 56 }, 
              height: { xs: 48, md: 56 }, 
              bgcolor: 'white', 
              borderRadius: 2, 
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center', 
              justifyContent: 'center', 
              boxShadow: '0px 4px 12px rgba(0,0,0,0.1)',
              flexShrink: 0,
              cursor: 'pointer'
            }}
          >
            <img
              src="/logo.png"
              alt="University Logo"
              style={{ height: '80%', width: '80%', objectFit: 'contain' }}
            />
          </Box>
        </Toolbar>
      </Container>

      {/* More Menu */}
      <Menu
        anchorEl={moreMenuAnchor}
        open={Boolean(moreMenuAnchor)}
        onClose={handleMoreMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 1.5,
            minWidth: 220,
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
            borderRadius: 2,
            overflow: 'visible',
            '&::before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'rgba(255, 255, 255, 0.98)',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
            '& .MuiMenuItem-root': {
              px: 2.5,
              py: 1.8,
              fontSize: '0.95rem',
              fontWeight: 500,
              color: 'text.primary',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.05)'
              }
            }
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {mainNavItems.map((item) => (
          <MenuItem key={item.name} onClick={() => handleMenuItemClick(item.path)}>
            {capitalize(item.name)}
          </MenuItem>
        ))}
      </Menu>

      {/* People Dropdown Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 1.5,
            minWidth: 220,
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
            borderRadius: 2,
            overflow: 'visible',
            '&::before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'rgba(255, 255, 255, 0.98)',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
            '& .MuiMenuItem-root': {
              px: 2.5,
              py: 1.8,
              fontSize: '0.95rem',
              fontWeight: 500,
              color: 'text.primary',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.05)'
              },
              '& .MuiListItemIcon-root': {
                minWidth: 40,
                color: 'primary.main'
              },
              '& .MuiListItemText-primary': {
                fontWeight: 500
              }
            },
            '& .MuiDivider-root': {
              my: 1,
              mx: 2,
              borderColor: 'rgba(0,0,0,0.08)'
            }
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => handleMenuItemClick('/people/students')}>
          <ListItemIcon>
            <School sx={{ fontSize: '1.3rem' }} />
          </ListItemIcon>
          <ListItemText 
            primary="Students" 
            secondary="Current Students"
            secondaryTypographyProps={{
              fontSize: '0.75rem',
              color: 'text.secondary',
              mt: 0.5
            }}
          />
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick('/people/faculty')}>
          <ListItemIcon>
            <Person sx={{ fontSize: '1.3rem' }} />
          </ListItemIcon>
          <ListItemText 
            primary="Faculty" 
            secondary="Teaching Staff"
            secondaryTypographyProps={{
              fontSize: '0.75rem',
              color: 'text.secondary',
              mt: 0.5
            }}
          />
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleMenuItemClick('/people/alumni')}>
          <ListItemIcon>
            <Groups sx={{ fontSize: '1.3rem' }} />
          </ListItemIcon>
          <ListItemText 
            primary="Alumni" 
            secondary="Graduates Network"
            secondaryTypographyProps={{
              fontSize: '0.75rem',
              color: 'text.secondary',
              mt: 0.5
            }}
          />
        </MenuItem>
      </Menu>

      {/* Drawer for mobile menu */}
      <Drawer anchor="right" open={drawerOpen} onClose={handleDrawerClose}>
        <Box sx={{ width: 270, p: 2, bgcolor: '#1F2937', height: '100%', color: '#fff', position: 'relative', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <IconButton
            onClick={handleDrawerClose}
            sx={{ position: 'absolute', top: 8, right: 8, color: '#fff' }}
            aria-label="Close menu"
          >
            <CloseIcon />
          </IconButton>
          <List sx={{ width: '100%', mt: 5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, maxHeight: '90vh', overflowY: 'auto' }}>
            {mainNavItems.map((item) => (
              <ListItem button component={RouterLink} to={item.path} onClick={handleDrawerClose} key={item.name} sx={{ color: '#fff', fontSize: '1.25rem', fontWeight: 700, py: 2, justifyContent: 'center' }}>
                {capitalize(item.name)}
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>
    </AppBar>
  );
};

export default Navbar; 